import { ChiroUpAPI } from '@chiroup/client-core/functions/ChiroUpAPI';

export type HomePageV2Meta = {
  publishedAt?: string;
  deployedBy?: string;
  lastEditedAt?: string;
  lastEditedBy?: string;
};

export type HomePageV2DeployResult = HomePageV2Meta & {
  /** True when the deploy also promoted the theme draft to live (i.e., a
   *  theme draft existed at deploy time). False when only content was
   *  promoted because the theme had no pending changes. */
  themeDeployed?: boolean;
};

export type TrialExperienceMeta = {
  publishedAt?: string;
  deployedBy?: string;
  lastEditedAt?: string;
  lastEditedBy?: string;
};

export type TrialExperienceDeployResult = TrialExperienceMeta & {
  /** True when the deploy promoted the trial-home draft to live. False
   *  when the deploy was a no-op because the home content was unchanged. */
  homeDeployed?: boolean;
  /** True when the deploy promoted the tour-overrides draft to live. */
  toursDeployed?: boolean;
  /** Set to `true` by the backend when both halves were already in sync
   *  with live, so nothing was written. UI can surface "Nothing to deploy". */
  noChanges?: boolean;
};

const appDataService = () => {
  const get = async (key: string): Promise<any> => {
    return ChiroUpAPI.get('api', `/app-data/${key}`, {});
  };

  const update = async (key: string, data: any) => {
    return ChiroUpAPI.put('api', `/app-data/${key}`, {
      body: data,
    });
  };

  // Promotes the current homepage draft (content + theme) to live and
  // stamps publishedAt. The backend handles both promotions in one
  // transaction so subscribers never see a content-vs-theme mismatch.
  const deployHomePageV2 = async (): Promise<HomePageV2DeployResult> => {
    return ChiroUpAPI.post('api', '/app-data/home-page-v2/deploy', {});
  };

  // Promotes the trial-experience drafts (trialHomeDraft +
  // trialTourOverridesDraft) to live. Same transactional guarantee — both
  // keys flip together so trial users never see content + tour copy out
  // of sync.
  const deployTrialExperience = async (): Promise<TrialExperienceDeployResult> => {
    return ChiroUpAPI.post('api', '/app-data/trial-experience/deploy', {});
  };

  return { get, update, deployHomePageV2, deployTrialExperience };
};

export default appDataService();
